import type {
  ModStatus,
  ProductKind,
  PublicationBulkModStatusIn,
  PublicationTree,
  TreeNode,
} from '@/api/generated/schemas';
import type { ApiErrorDetailItem } from '@/utils/apiError';
import {
  groupRefusals,
  type ItemErrorGroup,
} from '@/components/user_catalog/modStatusBulk';

/** Formats are split by kind so the modal reads "3 ebooks, 1 audiobook". */
export type FormatKind = 'ebooks' | 'audios';
export type DependentKind = 'publishers' | 'authors' | 'lectors' | FormatKind;

export const DEPENDENT_KINDS: DependentKind[] = [
  'publishers',
  'authors',
  'lectors',
  'ebooks',
  'audios',
];

const FORMAT_BY_PRODUCT: Record<ProductKind, FormatKind> = {
  ebook: 'ebooks',
  audio: 'audios',
};

export const isFormatKind = (kind: DependentKind): kind is FormatKind =>
  kind === 'ebooks' || kind === 'audios';

type Licensed = {
  has_active_distribution_period: boolean;
  /** Products only: a future period exists, so enabling ahead of it is fine. */
  is_upcoming_premiere?: boolean;
};

/** Enable-able now: an active period, or a premiere the backend accepts early. */
const licensed = (node: Licensed): boolean =>
  node.has_active_distribution_period || node.is_upcoming_premiere === true;

/**
 * Enabling needs a licence; any other status is always reachable.
 * Nodes without the flag (publisher, people) never block.
 */
const reachable = (node: TreeNode | Licensed, status: ModStatus): boolean =>
  status !== 'enabled' ||
  !('has_active_distribution_period' in node) ||
  licensed(node);

export interface UnlicensedItem {
  id: number;
  name: string;
  mod_status: ModStatus;
  href: string;
}

/** Publications and formats that cannot be enabled for lack of a licence. */
export const unlicensed = (
  trees: PublicationTree[]
): { publications: UnlicensedItem[]; products: UnlicensedItem[] } => ({
  publications: trees
    .filter((t) => !licensed(t.publication))
    .map((t) => ({
      ...t.publication,
      href: `/user_catalog/publications/${t.publication.id}`,
    })),
  products: trees.flatMap((t) =>
    (t.products ?? [])
      .filter((p) => !licensed(p))
      .map((p) => ({
        id: p.id,
        name: `${t.publication.name} · ${p.name}`,
        mod_status: p.mod_status,
        href: `/user_catalog/publications/${t.publication.id}`,
      }))
  ),
});

/** Kinds a visible publication depends on; required (and locked) when enabling. */
export const REQUIRED_ABOVE: DependentKind[] = ['publishers'];

/**
 * Pre-selected: the formats, plus — when enabling — everything required
 * above them, already enabled or not. People are context, opted into
 * explicitly, since they reach other publications.
 */
export const defaultInclude = (
  groups: DependentGroup[],
  status: ModStatus
): Set<DependentKind> =>
  new Set(
    groups
      .map((g) => g.kind)
      .filter(
        (kind) =>
          isFormatKind(kind) ||
          (status === 'enabled' && REQUIRED_ABOVE.includes(kind))
      )
  );

/** `formats` = the publications step when publications themselves are left alone. */
export type RunStepKind =
  | Exclude<DependentKind, FormatKind>
  | 'publications'
  | 'formats';

/** One request per entity kind; formats ride on the publications step. */
export interface RunStep {
  kind: RunStepKind;
  ids: number[];
  /** Objects this step changes (formats counted under publications). */
  count: number;
  /** How many of `count` are formats — 0 for every step but the last. */
  formats: number;
}

/**
 * Enable runs top-down (publisher first), any other status bottom-up,
 * so a partial failure never leaves a publication enabled under a
 * disabled publisher.
 */
export const buildRunSteps = (
  groups: DependentGroup[],
  include: ReadonlySet<DependentKind>,
  /** Publications that still differ from `status` (already filtered). */
  publicationIds: number[],
  includePublications = true,
  status: ModStatus = 'enabled'
): RunStep[] => {
  // Objects already at the target status are shown but never sent; a step
  // with `count: 0` is kept so the run can report it as skipped.
  const changing = (g: DependentGroup) =>
    g.items.filter((i) => i.mod_status !== status);
  const bulk = groups
    .filter((g) => !isFormatKind(g.kind) && include.has(g.kind))
    .map((g) => ({
      kind: g.kind as Exclude<DependentKind, FormatKind>,
      ids: changing(g).map((i) => i.id),
      count: changing(g).length,
      formats: 0,
    }));
  const formatGroups = groups.filter(
    (g) => isFormatKind(g.kind) && include.has(g.kind)
  );
  const formats = formatGroups.reduce((n, g) => n + changing(g).length, 0);
  const last: RunStep = includePublications
    ? {
        kind: 'publications',
        ids: publicationIds,
        count: publicationIds.length + formats,
        formats,
      }
    : { kind: 'formats', ids: [], count: formats, formats };
  const hasLast = includePublications || formatGroups.length > 0;
  if (!hasLast) return status === 'enabled' ? bulk : bulk.reverse();
  return status === 'enabled' ? [...bulk, last] : [last, ...bulk.reverse()];
};

/** Trees whose publication can actually take `status`. */
export const eligibleTrees = (
  trees: PublicationTree[],
  status: ModStatus
): PublicationTree[] => trees.filter((t) => reachable(t.publication, status));

export interface BlockedItem extends TreeNode {
  /** How many of the selected publications depend on this object. */
  usedBy: number;
  /** Admin path to open the object; products open their publication. */
  href: string;
}

export interface DependentGroup {
  kind: DependentKind;
  items: BlockedItem[];
}

export interface EnablePlan {
  groups: DependentGroup[];
  /** Publications with at least one non-enabled dependent. */
  blockedPublications: number;
  /** Total non-enabled dependent objects, deduped. */
  blockedObjects: number;
}

const dependents = (t: PublicationTree): [DependentKind, TreeNode][] => [
  ...(t.publisher
    ? [['publishers', t.publisher] as [DependentKind, TreeNode]]
    : []),
  ...(t.authors ?? []).map((a) => ['authors', a] as [DependentKind, TreeNode]),
  ...(t.lectors ?? []).map((l) => ['lectors', l] as [DependentKind, TreeNode]),
  ...(t.products ?? []).map(
    (p) => [FORMAT_BY_PRODUCT[p.kind], p] as [DependentKind, TreeNode]
  ),
];

/**
 * Dependents of the selected publications grouped by kind — all of them,
 * whatever their status, so the tree reads the same every time. The
 * counters still describe what actually blocks / differs.
 */
export const buildEnablePlan = (
  trees: PublicationTree[],
  status: ModStatus = 'enabled'
): EnablePlan => {
  const byKind = new Map<DependentKind, Map<number, BlockedItem>>(
    DEPENDENT_KINDS.map((k) => [k, new Map()])
  );
  let blockedPublications = 0;

  for (const tree of trees) {
    // Every reachable dependent is listed, so the whole tree is visible;
    // unlicensed formats are reported separately, not offered as a cascade.
    const listed = dependents(tree).filter(([, n]) => reachable(n, status));
    const blocking = listed.filter(([, n]) => n.mod_status !== status);
    if (blocking.length) blockedPublications += 1;
    for (const [kind, node] of listed) {
      const bucket = byKind.get(kind)!;
      const item = bucket.get(node.id);
      if (item) item.usedBy += 1;
      else
        bucket.set(node.id, {
          ...node,
          // Formats are only meaningful next to their publication.
          name: isFormatKind(kind)
            ? `${tree.publication.name} · ${node.name}`
            : node.name,
          usedBy: 1,
          href: isFormatKind(kind)
            ? `/user_catalog/publications/${tree.publication.id}`
            : `/user_catalog/${kind}/${node.id}`,
        });
    }
  }

  const groups = DEPENDENT_KINDS.map((kind) => ({
    kind,
    items: [...byKind.get(kind)!.values()].sort((a, b) => b.usedBy - a.usedBy),
  })).filter((g) => g.items.length > 0);

  return {
    groups,
    blockedPublications,
    blockedObjects: groups.reduce(
      (n, g) => n + g.items.filter((i) => i.mod_status !== status).length,
      0
    ),
  };
};

/** Products of `tree` whose format kind is included and that can take `status`. */
export const cascadedProducts = (
  tree: PublicationTree,
  include: ReadonlySet<DependentKind>,
  status: ModStatus
) =>
  (tree.products ?? []).filter(
    (p) =>
      include.has(FORMAT_BY_PRODUCT[p.kind]) &&
      p.mod_status !== status &&
      reachable(p, status)
  );

/**
 * Body for `PATCH /user_catalog/publications/update_status`: per
 * publication, its own new status (omitted when it stays as is) and the
 * formats changing with it, each with an explicit status.
 */
export const bulkPublicationItems = (
  trees: PublicationTree[],
  include: ReadonlySet<DependentKind>,
  status: ModStatus,
  includePublication = true
): PublicationBulkModStatusIn['items'] =>
  trees.flatMap((tree) => {
    const products = cascadedProducts(tree, include, status).map((p) => ({
      id: p.id,
      mod_status: status,
    }));
    const changesPublication =
      includePublication && tree.publication.mod_status !== status;
    // Nothing to change for this publication → leave it out of the request.
    if (!changesPublication && products.length === 0) return [];
    return [
      {
        publication_id: tree.publication.id,
        ...(changesPublication ? { mod_status: status } : {}),
        products,
      },
    ];
  });

/**
 * Objects a publications request changed: for every publication the backend
 * reports as updated, its own status (when it was sent) and each format sent
 * with it — an item is applied whole or refused whole.
 */
export const changedObjects = (
  items: PublicationBulkModStatusIn['items'],
  updated: number[]
): number => {
  const ids = new Set(updated);
  return items
    .filter((item) => ids.has(item.publication_id))
    .reduce(
      (n, item) => n + (item.mod_status ? 1 : 0) + (item.products?.length ?? 0),
      0
    );
};

/**
 * Refusals of a bulk publications request, grouped by reason. The backend
 * points at ``items.N`` (with ``ctx.kind`` for a format), which means nothing
 * to the moderator; each is named ``title · format`` instead.
 */
export const groupItemErrors = (
  items: PublicationBulkModStatusIn['items'],
  trees: PublicationTree[],
  errors: ApiErrorDetailItem[],
  formatLabel: (kind: string) => string
): ItemErrorGroup[] =>
  groupRefusals(errors, 'items', (index, { ctx }) => {
    const item = items[index];
    if (!item) return undefined;
    const name =
      trees.find((tr) => tr.publication.id === item.publication_id)?.publication
        .name ?? `#${item.publication_id}`;
    const kind =
      typeof ctx?.kind === 'string' ? ` · ${formatLabel(ctx.kind)}` : '';
    return `${name}${kind}`;
  });
